/*
 * Secure System Audit Request — branded internal booking/qualification flow.
 * Replaces the generic external booking page as the PRIMARY flow. Matches the
 * site's dark Nordic / glassmorphism / electric-blue aesthetic. Submits to
 * POST /api/audit-request, which stores the lead and triggers Resend emails.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, Loader2, ShieldCheck, CheckCircle2, Lock, CalendarClock } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/landing/LanguageToggle";
import { NodeGraph } from "@/components/landing/Decor";
import { AUDIT_PACKAGES } from "@/i18n/translations";
import { trackClick } from "@/lib/track";
import { BOOKING_PATH } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldClass =
  "w-full rounded-xl bg-[rgba(8,12,22,0.7)] border border-[var(--line)] px-4 py-3 text-[var(--white)] placeholder:text-[var(--muted-2)] font-sans text-sm outline-none transition-colors duration-300 focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]";

const labelClass = "block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider";

const EMPTY = {
  name: "", email: "", company: "", phone: "",
  system_type: "", business_criticality: "", budget_range: "",
  package_interest: "", message: "", company_url: "",
};

export const SecureSystemAuditPage = () => {
  const { t } = useLang();
  const a = t.audit;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Book a Secure System Audit · ReverseAI.dk";
    window.scrollTo(0, 0);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const src = new URLSearchParams(window.location.search).get("src") || "direct";
    const required = ["name", "email", "company", "system_type", "business_criticality", "budget_range", "package_interest", "message"];
    if (required.some((k) => !String(form[k]).trim())) {
      toast.error(a.errRequired);
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error(a.errEmail);
      return;
    }
    if (form.message.trim().length < 20) {
      toast.error(a.errMessage);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/audit-request`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim() || null,
        system_type: form.system_type,
        business_criticality: form.business_criticality,
        budget_range: form.budget_range,
        package_interest: form.package_interest,
        message: form.message.trim(),
        source_cta: src,
        company_url: form.company_url, // honeypot
      });
      trackClick("submit_secure_system_audit_request");
      setForm(EMPTY);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 429 ? a.errFail : a.errFail);
    } finally {
      setLoading(false);
    }
  };

  const Select = ({ k, options, testId }) => (
    <select className={`${fieldClass} appearance-none cursor-pointer`} value={form[k]} onChange={update(k)} data-testid={testId}>
      <option value="">{a.selectDefault}</option>
      {options.map((o) => {
        const value = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        return (
          <option key={value} value={value} className="bg-[#0b111e]">{label}</option>
        );
      })}
    </select>
  );

  return (
    <div className="min-h-screen bg-[var(--bg)] relative overflow-hidden" data-testid="audit-page">
      <div className="noise" />
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" style={{ zIndex: 0 }} />
      <div className="glow-orb animate-glow pointer-events-none" style={{ zIndex: 0, width: 560, height: 420, top: -80, left: "50%", transform: "translateX(-50%)", background: "var(--blue)", opacity: 0.18 }} />
      {/* decorative dependency graph, desktop only, never over content */}
      <NodeGraph className="absolute right-[2%] top-[24%] w-[280px] opacity-20 hidden xl:block animate-float-slow pointer-events-none" />

      {/* Top bar */}
      <header className="relative z-10 mx-auto max-w-5xl px-5 pt-7 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group" data-testid="audit-brand">
          <span className="grid place-items-center h-9 w-9 rounded-lg panel font-display font-bold text-sm text-white">RA</span>
          <span className="font-display font-semibold text-white text-sm">
            ReverseAI<span className="text-[var(--green)]">.dk</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted hover:text-white transition-colors" data-testid="audit-back-link">
            <ArrowLeft size={15} /> {a.back}
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-5 pt-12 pb-24">
        {done ? (
          <div className="glass-card depth-card rounded-3xl p-8 lg:p-12 text-center" data-testid="audit-success">
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-[rgba(54,226,160,0.12)] text-[var(--green)] mb-6 validate-pulse">
              <CheckCircle2 size={32} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white">{a.successTitle}</h1>
            <p className="mt-4 text-base text-muted leading-relaxed max-w-xl mx-auto">{a.successBody}</p>
            <div className="mt-6 inline-flex items-start gap-3 text-left glass rounded-2xl p-4 max-w-xl">
              <span className="grid place-items-center h-8 w-8 shrink-0 rounded-lg bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]"><ShieldCheck size={16} /></span>
              <p className="text-sm text-white/90 leading-relaxed">{a.successNext}</p>
            </div>
            <div className="mt-8">
              <Link to="/" className="btn-primary mx-auto" data-testid="audit-success-home">
                {a.successHome} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 mb-5">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--blue-bright)] border border-[var(--line-strong)] rounded-full px-3 py-1">
                <ShieldCheck size={13} /> {a.badge}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-[1.07] text-white" data-testid="audit-headline">
              {a.headline}
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted leading-relaxed max-w-2xl">{a.sub}</p>

            <div className="mt-6 flex flex-wrap gap-2.5">
              {a.trust.map((tr) => (
                <span key={tr} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[var(--muted-2)] border border-[var(--line)] rounded-full px-3 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)]" /> {tr}
                </span>
              ))}
            </div>

            <Link to={`${BOOKING_PATH}?src=audit`} className="mt-5 inline-flex items-center gap-2 text-sm text-[var(--blue-bright)] hover:text-white transition-colors" data-testid="audit-book-call-link">
              <CalendarClock size={15} /> {t.booking.auditPrompt} <ArrowUpRight size={14} />
            </Link>

            <form onSubmit={submit} className="mt-10 glass-card rounded-3xl p-6 lg:p-8 space-y-4" data-testid="audit-form" noValidate>
              {/* Honeypot — visually hidden, off-screen, not announced */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, height: 0, overflow: "hidden" }}>
                <label>Company URL<input type="text" tabIndex={-1} autoComplete="off" value={form.company_url} onChange={update("company_url")} data-testid="audit-honeypot" /></label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{a.labels.name} *</label>
                  <input className={fieldClass} value={form.name} onChange={update("name")} placeholder={a.placeholders.name} data-testid="audit-name-input" />
                </div>
                <div>
                  <label className={labelClass}>{a.labels.email} *</label>
                  <input type="email" className={fieldClass} value={form.email} onChange={update("email")} placeholder={a.placeholders.email} data-testid="audit-email-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{a.labels.company} *</label>
                  <input className={fieldClass} value={form.company} onChange={update("company")} placeholder={a.placeholders.company} data-testid="audit-company-input" />
                </div>
                <div>
                  <label className={labelClass}>{a.labels.phone} <span className="text-[var(--muted-2)] normal-case">({a.optional})</span></label>
                  <input className={fieldClass} value={form.phone} onChange={update("phone")} placeholder={a.placeholders.phone} data-testid="audit-phone-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{a.labels.systemType} *</label>
                  <Select k="system_type" options={a.systemTypes} testId="audit-systemtype-select" />
                </div>
                <div>
                  <label className={labelClass}>{a.labels.criticality} *</label>
                  <Select k="business_criticality" options={a.criticality} testId="audit-criticality-select" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{a.labels.budget} *</label>
                  <Select k="budget_range" options={a.budget} testId="audit-budget-select" />
                </div>
                <div>
                  <label className={labelClass}>{a.labels.pkg} *</label>
                  <Select k="package_interest" options={AUDIT_PACKAGES} testId="audit-package-select" />
                </div>
              </div>

              <div>
                <label className={labelClass}>{a.labels.message} *</label>
                <textarea rows={5} className={`${fieldClass} resize-none`} value={form.message} onChange={update("message")} placeholder={a.placeholders.message} data-testid="audit-message-input" />
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" data-testid="audit-submit-btn">
                {loading ? (<><Loader2 size={18} className="animate-spin" /> {a.sending}</>) : (<>{a.submit} <ArrowUpRight size={18} /></>)}
              </button>

              <p className="flex items-center justify-center gap-2 text-center text-[11px] text-[var(--muted-2)] font-mono">
                <Lock size={11} /> {a.footnote}
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
};
